import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FileSearch, UploadCloud, FileText, Check, X, Loader2, Trash2,
  TrendingUp, TrendingDown, AlertTriangle, Eye, Sparkles, ArrowRight, Building2, Info,
  BookOpen, ShieldCheck, HelpCircle, Lightbulb, Brain,
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { useApp } from "../context/AppContext";
import { useLang } from "../lib/i18n";
import { api } from "../lib/api";
import { Skeleton } from "../components/ui/skeleton";
import { NextStepCta } from "../components/NextStepCta";

const fmt = (v, unit) => {
  if (v == null || v === "") return "—";
  const n = Number(String(v).replace(",", "."));
  if (Number.isNaN(n)) return String(v);
  const s = n.toLocaleString("fr-FR", { maximumFractionDigits: Math.abs(n % 1) > 0 ? 1 : 0 });
  return unit ? `${s} ${unit}` : s;
};

// Libellé KPI traduit par clé canonique, repli sur le libellé backend si clé inconnue.
const kpiLabel = (key, fallback, t) => { const k = `kpi_${key}`; const v = t(k); return v === k ? (fallback || key) : v; };
const dimLabel = (dim, t) => { const k = `dim_${dim}`; const v = t(k); return v === k ? dim : v; };
const unitLabel = (unit, t) => (unit === "jours" ? t("unit_days") : unit === "x/an" ? t("unit_per_year") : unit);
const TRESO_ENUM = ["confortable", "correcte", "tendue", "critique"];
const dispVal = (v, t) => { const s = String(v ?? ""); return TRESO_ENUM.includes(s) ? t(`p360_treso_${s}`) : fmt(v); };

const PROV_CLS = {
  extrait: "bg-neon/15 text-neon border-neon/30",
  saisi: "bg-blue-500/15 text-blue-300 border-blue-400/30",
  calcul: "bg-violet-500/15 text-violet-300 border-violet-400/30",
};
const PROV_KEY = { extrait: "diag_observed", saisi: "diag_declared", calcul: "diag_calc" };
function ProvBadge({ source }) {
  const { t } = useLang();
  const src = PROV_CLS[source] ? source : "extrait";
  return <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full border ${PROV_CLS[src]}`} data-testid={`prov-${source}`}>{t(PROV_KEY[src])}</span>;
}

const NIVEAU = {
  eleve: { key: "lvl_eleve", cls: "bg-red-500/15 text-red-300 border-red-400/30", icon: AlertTriangle },
  surveiller: { key: "lvl_surveiller", cls: "bg-amber-500/15 text-amber-300 border-amber-400/30", icon: Eye },
  opportunite: { key: "lvl_opportunite", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30", icon: Sparkles },
};

// ----------------------------------------------------------- Rapport d'expert
const ANALYSE_NIVEAU = {
  important: { key: "lvl_important", cls: "bg-red-500/15 text-red-300 border-red-400/30", icon: AlertTriangle },
  a_surveiller: { key: "lvl_a_surveiller", cls: "bg-amber-500/15 text-amber-300 border-amber-400/30", icon: Eye },
  opportunite: { key: "lvl_opportunite", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30", icon: Sparkles },
};
const REL = {
  haute: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  moyenne: "bg-amber-500/15 text-amber-300 border-amber-400/30",
  basse: "bg-red-500/15 text-red-300 border-red-400/30",
};
const TYPE_BADGE = {
  fait: { key: "badge_fait", cls: "bg-neon/15 text-neon border-neon/30" },
  calcul: { key: "badge_calcul", cls: "bg-violet-500/15 text-violet-300 border-violet-400/30" },
  interpretation: { key: "badge_interp", cls: "bg-blue-500/15 text-blue-300 border-blue-400/30" },
  hypothese: { key: "badge_hypo", cls: "bg-amber-500/15 text-amber-300 border-amber-400/30" },
};

function TypeBadge({ type }) {
  const { t } = useLang();
  const b = TYPE_BADGE[type]; if (!b) return null;
  return <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full border ${b.cls}`} data-testid={`type-${type}`}>{t(b.key)}</span>;
}

function ReliabilityBadge({ level }) {
  const { t } = useLang();
  if (!level || !REL[level]) return null;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${REL[level]}`} data-testid={`reliability-${level}`}>
      <ShieldCheck size={12} /> {t("docs_reliability")}: {t(`rel_${level}`)}
    </span>
  );
}

function FactTraceability({ model }) {
  const { t } = useLang();
  const facts = model?.facts || [];
  const aggs = model?.aggregates || [];
  if (!facts.length && !aggs.length) return null;
  const scopeStr = (s) => { const e = Object.entries(s || {}); return e.length ? e.map(([k, v]) => `${k}: ${v}`).join(" · ") : t("docs_r_no_scope"); };
  const srcLabel = (src) => src === "calcul" ? t("docs_r_source_calcul") : t("docs_r_source_extrait");
  const srcCls = (src) => src === "calcul" ? "text-violet-300" : "text-neon";
  return (
    <Accordion type="single" collapsible className="lb-card px-4" data-testid="doc-r-facts">
      <AccordionItem value="facts" className="border-0">
        <AccordionTrigger className="text-sm" data-testid="doc-r-facts-trigger">{t("docs_r_facts_title")} ({facts.length + aggs.length})</AccordionTrigger>
        <AccordionContent>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">{t("docs_r_facts_help")}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {facts.map((f) => (
                  <tr key={f.fact_id} className="border-t border-white/5" data-testid={`fact-${f.fact_id}`}>
                    <td className="py-1.5 pr-3 whitespace-nowrap font-medium">{f.measure}</td>
                    <td className="py-1.5 pr-3 whitespace-nowrap font-display font-bold">{fmt(f.value, f.unit)}</td>
                    <td className="py-1.5 pr-3 text-xs text-[hsl(var(--muted-foreground))]">{scopeStr(f.scope)}</td>
                    <td className={`py-1.5 text-[10px] font-bold ${srcCls(f.source)}`}>{srcLabel(f.source)}</td>
                  </tr>
                ))}
                {aggs.map((a) => (
                  <tr key={a.fact_id} className="border-t border-white/5 opacity-70" data-testid={`agg-${a.fact_id}`}>
                    <td className="py-1.5 pr-3 whitespace-nowrap font-medium">{a.measure}</td>
                    <td className="py-1.5 pr-3 whitespace-nowrap font-display font-bold">{fmt(a.value, a.unit)}</td>
                    <td className="py-1.5 pr-3 text-xs text-[hsl(var(--muted-foreground))]">{scopeStr(a.scope)}</td>
                    <td className="py-1.5 text-[10px] uppercase text-amber-300/80">{a.kind}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function ExpertReport({ data }) {
  const { t } = useLang();
  const nav = useNavigate();
  const r = data.report || {};
  const model = data.model || {};
  const conf = model.confidence || data.confidence;
  const docId = data.document_id;
  const neutralized = (data.validation && data.validation.neutralized) || [];
  const [answers, setAnswers] = useState(() => {
    const m = {}; (data.field_answers || []).forEach((a) => { m[a.question] = a.answer; }); return m;
  });
  const [saving, setSaving] = useState(false);
  const saveAnswers = async () => {
    if (!docId) return;
    const payload = (r.questions_terrain || []).map((q) => ({ question: q, answer: (answers[q] || "").trim() })).filter((a) => a.answer);
    setSaving(true);
    try { await api.post(`/documents/${docId}/field-answers`, { answers: payload }); toast.success(t("docs_r_answer_saved")); }
    catch { toast.error(t("docs_err_read")); }
    setSaving(false);
  };
  return (
    <div data-testid="doc-expert-report" className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <BookOpen size={20} className="text-neon shrink-0" />
          <h2 className="font-display font-black text-lg sm:text-xl leading-tight" data-testid="doc-r-title">{r.titre || t("docs_report_title")}</h2>
        </div>
        <ReliabilityBadge level={conf} />
      </div>

      {r.synthese && (
        <section className="lb-card p-5" data-testid="doc-r-synthesis">
          <h3 className="text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">{t("docs_r_synthesis")}</h3>
          <p className="text-sm sm:text-base leading-relaxed">{r.synthese}</p>
        </section>
      )}

      {(r.faits_saillants || []).length > 0 && (
        <section data-testid="doc-r-highlights">
          <h3 className="font-display font-black text-base sm:text-lg mb-3">{t("docs_r_highlights")}</h3>
          <div className="space-y-2.5">
            {r.faits_saillants.map((e, i) => (
              <div key={i} className="lb-card p-4 flex items-start gap-3">
                <TypeBadge type={e.type} />
                <p className="text-sm flex-1">{e.enonce}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {(r.analyse || []).length > 0 && (
        <section data-testid="doc-r-analysis">
          <h3 className="font-display font-black text-base sm:text-lg mb-3">{t("docs_r_analysis")}</h3>
          <div className="grid gap-3 lg:grid-cols-2">
            {r.analyse.map((a, i) => {
              const n = ANALYSE_NIVEAU[a.niveau] || ANALYSE_NIVEAU.a_surveiller; const Icon = n.icon;
              return (
                <div key={i} className="lb-card p-4" data-testid={`analysis-${a.niveau}`}>
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-bold ${n.cls}`}><Icon size={12} />{t(n.key)}</span>
                    <TypeBadge type={a.type} />
                  </div>
                  {a.theme && <p className="font-semibold text-sm mb-1">{a.theme}</p>}
                  {a.lecture && <p className="text-sm text-[hsl(var(--muted-foreground))]">{a.lecture}</p>}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {(r.hypotheses || []).length > 0 && (
        <section data-testid="doc-r-hypotheses">
          <h3 className="font-display font-black text-base sm:text-lg mb-3">{t("docs_r_hypotheses")}</h3>
          <div className="space-y-2">
            {r.hypotheses.map((h, i) => (
              <div key={i} className="lb-card p-3.5 flex items-start gap-2.5 border-amber-400/20">
                <Lightbulb size={15} className="text-amber-400 shrink-0 mt-0.5" />
                <p className="text-sm">{h.enonce}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {(r.questions_terrain || []).length > 0 && (
        <section data-testid="doc-r-questions">
          <h3 className="font-display font-black text-base sm:text-lg mb-3">{t("docs_r_questions")}</h3>
          <div className="lb-card p-4 space-y-3">
            {r.questions_terrain.map((q, i) => (
              <div key={i} className="space-y-1.5">
                <div className="flex items-start gap-2.5">
                  <HelpCircle size={15} className="text-neon shrink-0 mt-0.5" />
                  <p className="text-sm">{q}</p>
                </div>
                {docId && (
                  <input value={answers[q] || ""} onChange={(e) => setAnswers((s) => ({ ...s, [q]: e.target.value }))}
                    data-testid={`field-answer-${i}`} placeholder={t("docs_r_answer_ph")}
                    className="w-full ml-6 rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-sm text-white focus:border-neon/50 outline-none" />
                )}
              </div>
            ))}
            {docId && (
              <div className="flex items-center gap-2 pt-1">
                <Button onClick={saveAnswers} disabled={saving} data-testid="field-answers-save"
                  variant="outline" className="rounded-full h-10 px-4 border-white/10 bg-white/5 text-sm">
                  {saving ? "…" : t("docs_r_answer_save")}
                </Button>
                <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-[hsl(var(--muted-foreground))]">{t("docs_r_declared")}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {(r.limites || []).length > 0 && (
        <section data-testid="doc-r-limits">
          <h3 className="font-display font-black text-base sm:text-lg mb-3">{t("docs_r_limits")}</h3>
          <div className="lb-card p-4 space-y-1.5">
            {r.limites.map((l, i) => (
              <p key={i} className="text-xs text-[hsl(var(--muted-foreground))] flex items-start gap-2"><Info size={13} className="shrink-0 mt-0.5" />{l}</p>
            ))}
          </div>
        </section>
      )}

      {neutralized.length > 0 && (
        <p className="text-[11px] text-amber-300/80 flex items-center gap-1.5" data-testid="doc-r-neutralized"><Info size={12} /> {t("docs_r_neutralized")}</p>
      )}

      <FactTraceability model={model} />

      {docId && (r.faits_saillants || []).length > 0 && (
        <Button onClick={() => nav("/app/diagnostic", { state: { enrichDoc: docId } })}
          data-testid="doc-r-use-in-diag"
          className="w-full rounded-full h-12 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold">
          <Brain size={16} className="mr-1.5" /> {t("docs_r_use_in_diag")}
        </Button>
      )}
    </div>
  );
}

function FailCard({ onReset }) {
  const { t } = useLang();
  return (
    <div className="lb-card p-6 text-center" data-testid="doc-fail">
      <div className="w-12 h-12 mx-auto rounded-2xl bg-amber-500/15 border border-amber-400/30 flex items-center justify-center mb-3">
        <AlertTriangle className="text-amber-400" size={22} />
      </div>
      <h2 className="font-display font-bold text-lg mb-2">{t("docs_fail_title")}</h2>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-1">{t("docs_fail_insufficient")}</p>
      <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">{t("docs_fail_help")}</p>
      <Button onClick={onReset} data-testid="doc-fail-retry" className="rounded-full h-11 px-6 bg-gradient-to-r from-primary to-neon text-white font-semibold">
        {t("docs_r_new")}
      </Button>
    </div>
  );
}

// ----------------------------------------------------------- Étape import
function ImportZone({ onFile, busy }) {
  const { t } = useLang();
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const pick = (f) => { if (f) onFile(f); };
  return (
    <div
      data-testid="doc-dropzone"
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
      className={`rounded-2xl border-2 border-dashed p-8 sm:p-12 text-center transition-colors ${drag ? "border-neon bg-neon/5" : "border-white/15 bg-white/[0.02]"}`}>
      <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-primary to-neon flex items-center justify-center mb-4">
        {busy ? <Loader2 className="animate-spin text-white" size={26} /> : <UploadCloud className="text-white" size={26} />}
      </div>
      <p className="font-display font-bold text-lg mb-1">{t("docs_drop")}</p>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">{t("docs_drop_or")}</p>
      <input ref={inputRef} type="file" accept=".pdf,.xlsx,.xls,.csv" className="hidden"
        data-testid="doc-file-input" onChange={(e) => pick(e.target.files?.[0])} />
      <Button onClick={() => inputRef.current?.click()} disabled={busy} data-testid="doc-choose-btn"
        className="rounded-full h-11 px-6 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold">
        {busy ? t("docs_analyzing") : t("docs_choose")}
      </Button>
      {busy
        ? <p className="text-xs text-neon mt-4 animate-pulse" data-testid="doc-analyzing-msg">{t("docs_analyzing_long")}</p>
        : <p className="text-xs text-[hsl(var(--muted-foreground))] mt-4">{t("docs_formats")}</p>}
    </div>
  );
}

// ----------------------------------------------------------- Étape revue
function ReviewStep({ result, filename, onCancel, onSaved }) {
  const { t } = useLang();
  const [rows, setRows] = useState(() =>
    (result.scalars || []).map((s) => ({ ...s, included: true, edited: false })));
  const [saving, setSaving] = useState(false);

  const setVal = (i, v) => setRows((p) => p.map((r, idx) => idx === i ? { ...r, value: v, edited: true, source: "saisi", confidence: "eleve" } : r));
  const toggle = (i) => setRows((p) => p.map((r, idx) => idx === i ? { ...r, included: !r.included } : r));

  const save = async () => {
    const scalars = rows.filter((r) => r.included && String(r.value ?? "").trim() !== "")
      .map(({ key, label, unit, value, source }) => ({ key, label, unit, value, source }));
    if (!scalars.length && !(result.breakdowns || []).length) {
      toast.error(t("docs_t_need_one"));
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/documents/save", {
        filename, doc_type: result.doc_type, scalars, breakdowns: result.breakdowns || [],
      });
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.status === 422 ? t("docs_err_422") : t("docs_err_analyze"));
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="doc-review">
      <div className="lb-card p-5 mb-4">
        <div className="flex items-center gap-2 mb-1"><FileText size={18} className="text-neon" /><h2 className="font-display font-bold text-lg">{t("docs_identified")}</h2></div>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">{t("docs_review_help")}</p>

        {rows.length === 0 && <p className="text-sm text-amber-400" data-testid="doc-no-scalars">{t("docs_no_scalars")}</p>}

        <div className="space-y-2.5">
          {rows.map((r, i) => (
            <div key={r.key + i} data-testid={`doc-row-${r.key}`}
              className={`rounded-xl border p-3 flex flex-wrap items-center gap-3 ${r.included ? "border-white/10 bg-white/[0.02]" : "border-white/5 bg-white/[0.01] opacity-50"}`}>
              <div className="flex-1 min-w-[120px]">
                <p className="text-sm font-medium">{kpiLabel(r.key, r.label, t)}</p>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <ProvBadge source={r.source} />
                  {r.confidence === "verifier" && <span className="text-[10px] px-2 py-0.5 rounded-full border bg-amber-500/15 text-amber-300 border-amber-400/30" data-testid="badge-verifier">{t("docs_verifier")}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <input value={r.value ?? ""} onChange={(e) => setVal(i, e.target.value)} data-testid={`doc-input-${r.key}`} disabled={!r.included}
                  className="w-28 rounded-lg bg-white/[0.04] border border-white/10 px-2.5 py-1.5 text-sm text-white text-right focus:border-neon/50 outline-none disabled:opacity-40" />
                <span className="text-xs text-[hsl(var(--muted-foreground))] w-6">{unitLabel(r.unit, t)}</span>
              </div>
              <button onClick={() => toggle(i)} data-testid={`doc-toggle-${r.key}`}
                className={`rounded-lg p-2 border transition-colors ${r.included ? "border-white/10 text-[hsl(var(--muted-foreground))] hover:text-red-300" : "border-neon/30 text-neon"}`}
                title={r.included ? t("docs_ignore") : t("docs_reintegrate")}>
                {r.included ? <X size={15} /> : <Check size={15} />}
              </button>
            </div>
          ))}
        </div>

        {(result.breakdowns || []).map((bd, bi) => (
          <div key={bi} className="mt-4" data-testid={`doc-breakdown-preview-${bi}`}>
            <p className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">{t("docs_breakdown_detected", { dim: dimLabel(bd.dimension, t), n: bd.rows.length })}</p>
            <div className="rounded-xl border border-white/10 overflow-hidden text-sm">
              {bd.rows.slice(0, 6).map((row, ri) => (
                <div key={ri} className="flex justify-between px-3 py-1.5 border-b border-white/5 last:border-0">
                  <span>{row.name}</span>
                  <span className="text-[hsl(var(--muted-foreground))]">{row.ca != null ? fmt(row.ca, "€") : "—"}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <Button onClick={onCancel} variant="outline" className="rounded-full h-12 px-4 border-white/10 bg-white/5" data-testid="doc-cancel">{t("cancel")}</Button>
        <Button onClick={save} disabled={saving} data-testid="doc-validate"
          className="flex-1 rounded-full h-12 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold">
          {saving ? <><Loader2 className="animate-spin mr-2" size={16} /> {t("docs_analyzing")}</> : <>{t("docs_validate")} <ArrowRight size={16} className="ml-1.5" /></>}
        </Button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------- Étape résultat
function KpiCard({ label, tid, value, unit, source, delta, deltaUnit }) {
  const up = typeof delta === "number" && delta > 0;
  const down = typeof delta === "number" && delta < 0;
  return (
    <div className="lb-card p-4" data-testid={`kpi-${tid || label}`}>
      <p className="text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">{label}</p>
      <p className="font-display font-black text-xl mb-1.5">{fmt(value, unit)}</p>
      <div className="flex items-center gap-2 flex-wrap">
        {source && <ProvBadge source={source} />}
        {typeof delta === "number" && (
          <span className={`inline-flex items-center gap-1 text-xs ${up ? "text-emerald-400" : down ? "text-red-400" : "text-[hsl(var(--muted-foreground))]"}`}>
            {up ? <TrendingUp size={13} /> : down ? <TrendingDown size={13} /> : null}{fmt(delta, deltaUnit)}
          </span>
        )}
      </div>
    </div>
  );
}

function ResultView({ doc, onEnrich }) {
  const { t } = useLang();
  const interp = doc.interpretation || {};
  const scalars = useMemo(() => doc.scalars || [], [doc]);
  const computed = useMemo(() => doc.computed || [], [doc]);
  const byKey = useMemo(() => {
    const m = {}; scalars.forEach((s) => { m[s.key] = s; }); computed.forEach((c) => { m[c.key] = c; });
    return m;
  }, [scalars, computed]);

  const primaryKeys = ["ca", "marge_taux", "marge_euros", "resultat", "stock_valeur", "tresorerie", "panier_moyen", "nb_clients", "charges", "rotation_stock"];
  const cards = primaryKeys.filter((k) => byKey[k] != null).map((k) => byKey[k]);
  const evo = computed.find((c) => c.key === "evolution_ca");
  const obj = computed.find((c) => c.key === "objectif_atteinte");
  const chargeWeights = computed.filter((c) => c.key.endsWith("_pct_ca"));

  return (
    <div data-testid="doc-result" className="space-y-6">
      {/* Synthèse */}
      <section>
        <h2 className="font-display font-black text-lg mb-3">{t("docs_overview")}</h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {cards.map((c) => (
            <KpiCard key={c.key} tid={c.key} label={kpiLabel(c.key, c.label, t)} value={c.value} unit={unitLabel(c.unit, t)} source={c.source} />
          ))}
          {evo && <KpiCard tid="evolution_ca" label={kpiLabel("evolution_ca", evo.label, t)} value={evo.value} unit="%" source="calcul" delta={evo.delta_eur} deltaUnit="€" />}
          {obj && <KpiCard tid="objectif_atteinte" label={kpiLabel("objectif_atteinte", obj.label, t)} value={obj.value} unit="%" source="calcul" delta={obj.delta_eur} deltaUnit="€" />}
        </div>
      </section>

      {/* Ce que LocalBoost observe */}
      {(interp.observations || []).length > 0 && (
        <section data-testid="doc-observations">
          <h2 className="font-display font-black text-lg mb-3">{t("docs_observes")}</h2>
          <div className="space-y-3">
            {interp.observations.map((o, i) => (
              <div key={i} className="lb-card p-4">
                <p className="text-sm mb-2"><span className="text-[10px] uppercase tracking-wide text-neon font-bold mr-2">{t("docs_fact")}</span>{o.fait}</p>
                {o.interpretation && <p className="text-sm text-[hsl(var(--muted-foreground))] mb-2"><span className="text-[10px] uppercase tracking-wide text-blue-300 font-bold mr-2">{t("docs_interp")}</span>{o.interpretation}</p>}
                {o.recommandation && <p className="text-sm text-[hsl(var(--muted-foreground))]"><span className="text-[10px] uppercase tracking-wide text-violet-300 font-bold mr-2">{t("docs_reco")}</span>{o.recommandation}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Ce qui mérite votre attention */}
      {(interp.attention || []).length > 0 && (
        <section data-testid="doc-attention">
          <h2 className="font-display font-black text-lg mb-3">{t("docs_attention")}</h2>
          <div className="grid gap-3 lg:grid-cols-2">
            {interp.attention.map((a, i) => {
              const n = NIVEAU[a.niveau] || NIVEAU.surveiller; const Icon = n.icon;
              return (
                <div key={i} className="lb-card p-4" data-testid={`attention-${a.niveau}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-bold ${n.cls}`}><Icon size={12} />{t(n.key)}</span>
                  </div>
                  <p className="font-semibold text-sm mb-1">{a.titre}</p>
                  {a.ce_qui_se_passe && <p className="text-sm text-[hsl(var(--muted-foreground))] mb-1">{a.ce_qui_se_passe}</p>}
                  {a.pourquoi && <p className="text-xs text-[hsl(var(--muted-foreground))] mb-1.5"><b className="text-white/80">{t("docs_why")}&nbsp;:</b> {a.pourquoi}</p>}
                  {a.indicateur && <p className="text-xs text-neon/90"><b>{t("docs_indicator")}&nbsp;:</b> {a.indicateur}</p>}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Points forts */}
      {(interp.points_forts || []).length > 0 && (
        <section data-testid="doc-strengths">
          <h2 className="font-display font-black text-lg mb-3">{t("docs_strengths")}</h2>
          <div className="grid gap-3 lg:grid-cols-2">
            {interp.points_forts.map((p, i) => (
              <div key={i} className="lb-card p-4 border-emerald-400/20">
                <div className="flex items-center gap-2 mb-1"><Check size={15} className="text-emerald-400" /><p className="font-semibold text-sm">{p.titre}</p></div>
                {p.preuve && <p className="text-xs text-[hsl(var(--muted-foreground))]">{p.preuve}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Où part mon argent (compte d'exploitation) */}
      {chargeWeights.length > 0 && (
        <section data-testid="doc-exploitation">
          <h2 className="font-display font-black text-lg mb-3">{t("docs_money")}</h2>
          <div className="lb-card p-4 space-y-2">
            {chargeWeights.map((c) => (
              <div key={c.key}>
                <div className="flex justify-between text-sm mb-1"><span>{kpiLabel(c.key, c.label, t)}</span><span className="font-semibold">{fmt(c.value, "%")}</span></div>
                <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-primary to-neon" style={{ width: `${Math.min(100, c.value)}%` }} /></div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Analyse commerciale par dimension */}
      {(doc.breakdowns || []).map((bd, bi) => (
        <BreakdownAnalysis key={bi} bd={bd} />
      ))}

      {/* Enrichir le Profil 360° */}
      <section className="lb-card p-4">
        <div className="flex items-center gap-2 mb-2"><Building2 size={16} className="text-neon" /><p className="font-semibold text-sm">{t("docs_enrich_title")}</p></div>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">{t("docs_enrich_help")}</p>
        <Button onClick={onEnrich} data-testid="doc-enrich-360" className="rounded-full h-11 px-5 bg-white/5 border border-white/10 hover:border-neon/40 text-sm font-semibold">
          {t("docs_enrich_btn")} <ArrowRight size={15} className="ml-1.5" />
        </Button>
      </section>
    </div>
  );
}

function BreakdownAnalysis({ bd }) {
  const { t } = useLang();
  const rows = [...(bd.rows || [])];
  const hasMarge = rows.some((r) => r.marge_euros != null);
  const chartData = rows.slice(0, 8).map((r) => ({ name: r.name, CA: r.ca || 0, taux: r.taux_marge_ligne }));
  const sortedByContrib = hasMarge ? [...rows].sort((a, b) => (b.contrib_marge || 0) - (a.contrib_marge || 0)) : rows;
  const underperf = hasMarge ? rows.filter((r) => bd.avg_taux_marge != null && r.taux_marge_ligne != null && r.taux_marge_ligne < bd.avg_taux_marge && (r.poids_ca || 0) >= 8) : [];
  const dim = dimLabel(bd.dimension, t);

  return (
    <section data-testid="doc-commercial">
      <h2 className="font-display font-black text-lg mb-1">{t("docs_analysis_by", { dim })}</h2>
      <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">{t("docs_best_perf")}</p>

      <div className="lb-card p-4 mb-3">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 8 }}>
            <XAxis dataKey="name" tick={{ fill: "#9aa", fontSize: 11 }} interval={0} angle={-12} textAnchor="end" height={50} />
            <YAxis tick={{ fill: "#9aa", fontSize: 11 }} />
            <Tooltip contentStyle={{ background: "#15151c", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 }}
              formatter={(v, k) => [k === "CA" ? fmt(v, "€") : v, k]} />
            <Bar dataKey="CA" radius={[6, 6, 0, 0]}>
              {chartData.map((d, i) => (
                <Cell key={i} fill={bd.avg_taux_marge != null && d.taux != null && d.taux < bd.avg_taux_marge ? "#f59e0b" : "#00E5FF"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">{t("docs_orange_note", { v: fmt(bd.avg_taux_marge, "%") })}</p>
      </div>

      {underperf.length > 0 && (
        <div className="lb-card p-4 mb-3 border-amber-400/20" data-testid="doc-underperf">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle size={15} className="text-amber-400" /><p className="font-semibold text-sm">{t("docs_underperf")}</p></div>
          {underperf.map((r, i) => (
            <p key={i} className="text-xs text-[hsl(var(--muted-foreground))]">
              <b className="text-white/90">{r.name}</b> {t("docs_underperf_line", { name: "", poids: fmt(r.poids_ca, "%"), taux: fmt(r.taux_marge_ligne, "%"), contrib: fmt(r.contrib_marge, "%") })}
            </p>
          ))}
        </div>
      )}

      <Accordion type="single" collapsible className="lb-card px-4">
        <AccordionItem value="detail" className="border-0">
          <AccordionTrigger className="text-sm" data-testid="doc-breakdown-detail">{t("docs_see_detail", { dim })}</AccordionTrigger>
          <AccordionContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase text-[hsl(var(--muted-foreground))] text-left">
                    <th className="py-1.5 pr-3">{t("docs_col_name")}</th><th className="py-1.5 pr-3">{t("docs_col_ca")}</th>
                    {hasMarge && <><th className="py-1.5 pr-3">{t("docs_col_pct")}</th><th className="py-1.5 pr-3">{t("docs_col_taux")}</th><th className="py-1.5">{t("docs_col_contrib")}</th></>}
                  </tr>
                </thead>
                <tbody>
                  {sortedByContrib.map((r, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="py-1.5 pr-3">{r.name}</td>
                      <td className="py-1.5 pr-3">{fmt(r.ca, "€")}</td>
                      {hasMarge && <>
                        <td className="py-1.5 pr-3">{fmt(r.poids_ca, "%")}</td>
                        <td className={`py-1.5 pr-3 ${bd.avg_taux_marge != null && r.taux_marge_ligne != null && r.taux_marge_ligne < bd.avg_taux_marge ? "text-amber-400" : ""}`}>{fmt(r.taux_marge_ligne, "%")}</td>
                        <td className="py-1.5">{fmt(r.contrib_marge, "%")}</td>
                      </>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </section>
  );
}

// ----------------------------------------------------------- Page
export default function DocumentAnalysis() {
  const nav = useNavigate();
  const { demo } = useApp();
  const { t } = useLang();
  const [stage, setStage] = useState("idle"); // idle | report | fail | result(legacy)
  const [busy, setBusy] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [filename, setFilename] = useState("");
  const [doc, setDoc] = useState(null);
  const [report, setReport] = useState(null); // {report, model, validation, confidence, filename}
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(true);
  const [conflict, setConflict] = useState(null); // {conflicts:[], resolutions:{}}

  useEffect(() => { if (demo) nav("/app/dashboard", { replace: true }); }, [demo, nav]);

  const loadHistory = async () => {
    try { const { data } = await api.get("/documents"); setHistory(data || []); } catch { /* non connecté */ }
    finally { setHistLoading(false); }
  };
  useEffect(() => { loadHistory(); }, []);

  // Récupération après coupure passerelle (502/504) : l'IA peut dépasser le timeout
  // ingress (~60s) alors que le backend termine et ENREGISTRE le rapport. On sonde alors
  // GET /documents pour retrouver le rapport tout juste persisté (aucune double analyse).
  const pollForReport = async (fname, startedAtIso) => {
    const started = new Date(startedAtIso).getTime();
    for (let i = 0; i < 20; i++) {
      await new Promise((r) => setTimeout(r, 6000));
      try {
        const { data } = await api.get("/documents");
        const hit = (data || []).find((d) => d.filename === fname && d.has_report && new Date(d.created_at).getTime() >= started - 2000);
        if (hit) {
          setHistory(data);
          const { data: full } = await api.get(`/documents/${hit.document_id}`);
          if (full.expert_report) {
            setReport({ report: full.expert_report, model: full.expert_model, validation: full.expert_validation, confidence: (full.expert_model || {}).confidence, filename: full.filename, document_id: full.document_id, field_answers: full.field_answers || [] });
            setStage("report");
            window.scrollTo(0, 0);
            return true;
          }
        }
      } catch { /* retry */ }
    }
    return false;
  };

  const onFile = async (file) => {
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "xlsx", "xls", "csv"].includes(ext)) {
      toast.error(t("docs_t_unsupported"));
      return;
    }
    setBusy(true); setFilename(file.name);
    const startedAt = new Date().toISOString();
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/documents/expert-analyze", form, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 150000,
      });
      if (data.ok === false) {
        setStage("fail");
      } else {
        setReport({ report: data.report, model: data.model_summary, validation: data.validation, confidence: data.confidence, filename: data.filename, document_id: data.document_id, field_answers: [] });
        setStage("report");
        loadHistory();
        toast.success(t("docs_t_ready"));
      }
      window.scrollTo(0, 0);
    } catch (e) {
      const s = e?.response?.status;
      if ([415, 413, 400, 422].includes(s)) {
        const msg = s === 415 ? t("docs_err_415") : s === 413 ? t("docs_err_413") : s === 400 ? t("docs_err_400") : t("docs_err_422");
        toast.error(msg);
      } else {
        // Coupure passerelle (502/504) ou réseau : le backend a pu terminer et enregistrer.
        // On sonde plus longtemps (jusqu'à ~120s) pour ne JAMAIS montrer d'erreur technique.
        const recovered = await pollForReport(file.name, startedAt);
        if (recovered) toast.success(t("docs_t_ready"));
        else toast.info(t("docs_processing"));
      }
    } finally { setBusy(false); }
  };

  const openDoc = async (id) => {
    try {
      const { data } = await api.get(`/documents/${id}`);
      if (data.expert_report) {
        setReport({ report: data.expert_report, model: data.expert_model, validation: data.expert_validation, confidence: (data.expert_model || {}).confidence, filename: data.filename, document_id: data.document_id, field_answers: data.field_answers || [] });
        setStage("report");
      } else {
        setDoc(data); setStage("result"); // rétrocompat anciens documents
      }
      window.scrollTo(0, 0);
    } catch { toast.error(t("docs_t_not_found")); }
  };
  const deleteDoc = async (id, e) => {
    e.stopPropagation();
    try { await api.delete(`/documents/${id}`); loadHistory(); toast.success(t("docs_t_deleted")); }
    catch { toast.error(t("docs_t_delete_fail")); }
  };

  const enrich = async (resolutions) => {
    try {
      const { data } = await api.post("/documents/enrich-360", { document_id: doc.document_id, resolutions: resolutions || {} });
      if (data.status === "conflicts") {
        setConflict({ conflicts: data.conflicts, resolutions: {} });
        return;
      }
      setConflict(null);
      const n = Object.keys(data.applied || {}).length;
      toast.success(n ? t("docs_t_enrich_ok", { n }) : t("docs_t_enrich_none"));
    } catch (e) {
      toast.error(t("docs_err_enrich"));
    }
  };

  const reset = () => { setStage("idle"); setAnalysis(null); setDoc(null); setReport(null); setFilename(""); };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto" data-testid="document-analysis">
        <div className="flex items-center gap-2 mb-1"><FileSearch size={20} className="text-neon" /><h1 className="font-display font-black text-2xl">{t("docs_title")}</h1></div>
        <p className="text-neon font-display font-bold text-base mb-1">{t("docs_tagline")}</p>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6 max-w-2xl">
          {t("docs_intro")}
        </p>

        {stage === "idle" && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <ImportZone onFile={onFile} busy={busy} />
            {histLoading ? (
              <div className="mt-8" data-testid="doc-history-loading">
                <Skeleton className="h-5 w-32 mb-3" />
                <div className="space-y-2">
                  {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-2xl" />)}
                </div>
              </div>
            ) : history.length > 0 && (
              <div className="mt-8" data-testid="doc-history">
                <h2 className="font-display font-bold text-base mb-3">{t("docs_history")}</h2>
                <div className="space-y-2">
                  {history.map((h) => (
                    <div key={h.document_id} onClick={() => openDoc(h.document_id)} data-testid={`doc-hist-${h.document_id}`}
                      className="lb-card p-3 flex items-center gap-3 cursor-pointer hover:border-neon/30 transition-colors">
                      <FileText size={18} className="text-neon shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{h.filename || t("docs_document")}</p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">{new Date(h.created_at).toLocaleDateString()}{h.has_report ? ` · ${t("docs_report_title")}` : ` · ${h.kpi_count} ${t("docs_kpis")}`}</p>
                      </div>
                      <button onClick={(e) => deleteDoc(h.document_id, e)} data-testid={`doc-del-${h.document_id}`} className="inline-flex items-center justify-center min-w-[40px] min-h-[40px] sm:min-w-0 sm:min-h-0 p-2 text-[hsl(var(--muted-foreground))] hover:text-red-400"><Trash2 size={15} /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {stage === "report" && report && (
          <div>
            <button onClick={reset} className="text-sm text-[hsl(var(--muted-foreground))] hover:text-white mb-4 inline-flex items-center gap-1" data-testid="doc-new"><UploadCloud size={15} /> {t("docs_r_new")}</button>
            <ExpertReport data={report} />
            <NextStepCta testid="doc-next-step"
              description={t("nextstep_docs_desc")} ctaLabel={t("cta_next_diagnostic")}
              onClick={() => nav("/app/diagnostic")} />
          </div>
        )}

        {stage === "fail" && (
          <div>
            <button onClick={reset} className="text-sm text-[hsl(var(--muted-foreground))] hover:text-white mb-4 inline-flex items-center gap-1" data-testid="doc-new"><UploadCloud size={15} /> {t("docs_r_new")}</button>
            <FailCard onReset={reset} />
          </div>
        )}

        {stage === "result" && doc && (
          <div>
            <button onClick={reset} className="text-sm text-[hsl(var(--muted-foreground))] hover:text-white mb-4 inline-flex items-center gap-1" data-testid="doc-new"><UploadCloud size={15} /> {t("docs_import_another")}</button>
            <ResultView doc={doc} onEnrich={() => enrich()} />
            <NextStepCta testid="doc-next-step"
              description={t("nextstep_docs_desc")} ctaLabel={t("cta_next_diagnostic")}
              onClick={() => nav("/app/diagnostic")} />
          </div>
        )}

        {/* Dialog conflits Profil 360° */}
        <Dialog open={!!conflict} onOpenChange={(o) => { if (!o) setConflict(null); }}>
          <DialogContent className="glass border-white/10 w-[calc(100%-2rem)] max-w-md rounded-2xl" data-testid="doc-conflict-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Info size={18} className="text-amber-400" /> {t("docs_conflict_title")}</DialogTitle></DialogHeader>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">{t("docs_conflict_help")}</p>
            <div className="space-y-3 max-h-[50vh] overflow-y-auto">
              {conflict?.conflicts.map((c) => (
                <div key={c.field} className="rounded-xl border border-white/10 p-3" data-testid={`conflict-${c.field}`}>
                  <p className="text-sm font-medium mb-2">{(() => { const k = `p360f_${c.field}`; const v = t(k); return v === k ? c.field : v; })()}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => setConflict((p) => ({ ...p, resolutions: { ...p.resolutions, [c.field]: c.existing } }))}
                      data-testid={`conflict-${c.field}-existing`}
                      className={`rounded-lg border p-2 text-xs text-left ${conflict.resolutions[c.field] === c.existing ? "border-neon/50 bg-neon/10" : "border-white/10 bg-white/[0.02]"}`}>
                      <span className="block text-[10px] text-[hsl(var(--muted-foreground))]">{t("docs_current_profile")}</span>{dispVal(c.existing, t)}
                    </button>
                    <button onClick={() => setConflict((p) => ({ ...p, resolutions: { ...p.resolutions, [c.field]: c.from_document } }))}
                      data-testid={`conflict-${c.field}-document`}
                      className={`rounded-lg border p-2 text-xs text-left ${conflict.resolutions[c.field] === c.from_document ? "border-neon/50 bg-neon/10" : "border-white/10 bg-white/[0.02]"}`}>
                      <span className="block text-[10px] text-[hsl(var(--muted-foreground))]">{t("docs_from_document")}</span>{dispVal(c.from_document, t)}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <Button onClick={() => enrich(conflict.resolutions)}
              disabled={conflict && Object.keys(conflict.resolutions).length < conflict.conflicts.length}
              data-testid="conflict-apply"
              className="rounded-full h-11 bg-gradient-to-r from-primary to-neon text-white font-semibold disabled:opacity-40">
              {t("docs_save_choices")}
            </Button>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
